/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useMemo } from "react";
import { IResourceDescription } from "@adapptio/lib-core";
import { BEM } from "@adapptio/lib-components";
import { ICON_SIZE } from "../Icon";
import { BreadcrumbsSeparator } from "../Breadcrumbs/BreadcrumbsSeparator";
import { ResourcePathBreadcrumbsItem } from "./ResourcePathBreadcrumbsItem";
import { IResourcePathBreadcrumbItem } from "./ResourcePathBreadcrumbsTypes";
import { BreadcrumbsCollapsed } from "../Breadcrumbs/BreadcrumbsCollapsed";

const MAX_VISIBLE_CRUMBS_LENGTH_DEFAULT = 3;

export interface IResourcePathBreadcrumbsProps {
	/** Parent list of resources (from ResourceService.getResourceParentList) */
	parentList: IResourceDescription[];
	/** Current resource name */
	resourceName: string;
	/** Current resource ID */
	resourceId?: string;
	/** Maximum number of visible breadcrumbs */
	maxVisibleCrumbs?: number;
	/** Custom class name */
	className?: string;
	/** Aria label for accessibility */
	ariaLabel?: string;
	/** Whether to enable popup navigation on breadcrumb items */
	enablePopup?: boolean;
	/** Callback when a resource is selected from popup */
	onResourceSelect?: (resourceId: string) => void;
	/** Separator icon size */
	separatorSize?: ICON_SIZE;
}

function insertSeparators(
	items: React.ReactNode[],
	listItemClassName: string,
	separatorSize: ICON_SIZE
): React.ReactNode[] {
	return items.reduce<React.ReactNode[]>((acc, current, index) => {
		if (index < items.length - 1) {
			acc = acc.concat(
				current,
				<BreadcrumbsSeparator
					key={`separator-${index}`}
					className={listItemClassName}
					separatorSize={separatorSize}
				/>
			);
		} else {
			acc.push(current);
		}

		return acc;
	}, []);
}

export const ResourcePathBreadcrumbs: React.FC<IResourcePathBreadcrumbsProps> = ({
	parentList,
	resourceName,
	resourceId,
	maxVisibleCrumbs,
	className,
	ariaLabel = "breadcrumb",
	enablePopup = true,
	onResourceSelect,
	separatorSize = ICON_SIZE.SMALL
}) => {
	const baseClass = BEM("edt-breadcrumbs");

	// Build breadcrumb items from parent list and current resource
	const breadcrumbItems = useMemo<IResourcePathBreadcrumbItem[]>(() => {
		const items: IResourcePathBreadcrumbItem[] = [];

		// Build path incrementally
		let currentPath = "";

		if (parentList) {
			for (let i = 0; i < parentList.length; i++) {
				const parent = parentList[i];
				if (parent) {
					// Build path for this level
					if (currentPath) {
						currentPath = `${currentPath}/${parent.name}`;
					} else {
						currentPath = parent.name;
					}

					items.push({
						path: parent.basePath || "",
						label: parent.name,
						resourceId: parent.id,
						descriptor: parent,
						isCurrent: false
					});
				}
			}
		}

		// Add current resource
		items.push({
			path: currentPath,
			label: resourceName || "Unknown",
			resourceId: resourceId || null,
			descriptor: null,
			isCurrent: true
		});

		return items;
	}, [ parentList, resourceName, resourceId ]);

	const _maxCrumbsLength = maxVisibleCrumbs ?? MAX_VISIBLE_CRUMBS_LENGTH_DEFAULT;

	const visibleItems = breadcrumbItems.slice(-_maxCrumbsLength);
	const collapsedItems = breadcrumbItems.slice(0, -_maxCrumbsLength);

	const crumbs = useMemo(() => {
		return visibleItems.map((item, index) => (
			<li className={baseClass.element("list-item").toClassName()} key={`crumb-${index}`}>
				<ResourcePathBreadcrumbsItem
					item={item}
					isLast={index === visibleItems.length - 1}
					enablePopup={enablePopup}
					onResourceSelect={onResourceSelect}
				/>
			</li>
		));
	}, [ visibleItems, enablePopup, onResourceSelect ]);

	// Handle collapsed items (converted to simple breadcrumb items for BreadcrumbsCollapsed)
	if (collapsedItems.length > 0) {
		const collapsedBreadcrumbItems = collapsedItems.map((item) => ({
			label: item.label,
			onClick: undefined // Collapsed items don't have popup, they use the dropdown
		}));

		crumbs.unshift(
			<BreadcrumbsCollapsed key="breadcrumbs-collapsed" items={collapsedBreadcrumbItems} />
		);
	}

	return (
		<nav className={`${baseClass.toClassName()} ${className || ""}`} aria-label={ariaLabel}>
			<ol className={baseClass.element("list").toClassName()}>
				{insertSeparators(crumbs, baseClass.element("list-item").toClassName(), separatorSize)}
			</ol>
		</nav>
	);
};

ResourcePathBreadcrumbs.displayName = "ResourcePathBreadcrumbs";
